<template>
  <div>
    <div class="mobile-tabs-container" v-if="shouldShowMobileTabs">
      <div class="mobile-tabs">
        <div 
          v-for="tab in tabs" 
          :key="tab.id"
          class="mobile-tab"
          :class="{ 'active': isActiveTab(tab) }"
          @click="onTabClick(tab)"
        >
          <app-icon
            :name="tab.icon"
            size="20"
            :type="isActiveTab(tab) ? 'fill' : 'outline'"
            :color="isActiveTab(tab) ? '#005DCA' : '#807D7B'"
          />
          <span class="tab-label">{{ tab.label }}</span>
        </div>
      </div>
    </div>
  </div>                                      
</template>

<script>
import AppIcon from '@/components/app-icon/';
import { preloadRelatedRoutes } from '@/utils/route-preloader';
import { applyMobileOptimizations, removeMobileOptimizations } from '@/utils/mobile-css-optimizer';

export default {
  name: 'AppMobileTabs',
  components: {
    AppIcon,
  },
  data() {
    return {
      windowWidth: window.innerWidth || 1024, // Fallback value
      resizeTimeout: null,
      tabs: [
        {
          id: 'dashboard',
          label: 'Thống kê chung',
          icon: 'analytics',
          route: 'main.dashboard',
        },
        {
          id: 'incoming',
          label: 'Hóa đơn đầu vào',
          icon: 'income_file',
          route: 'main.expense',
        },
        {
          id: 'issued',
          label: 'Hóa đơn đã phát hành',
          icon: 'issued',
          route: 'main.revenue',
        },
        {
          id: 'draft',
          label: 'Hóa đơn chưa phát hành',
          icon: 'issuing',
          route: 'main.status.draft',
        },
      ],
    };
  },
  mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.handleResize);
    
    // Apply mobile CSS optimizations
    if (this.windowWidth <= 768) {
      applyMobileOptimizations();
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    // Cleanup mobile optimizations
    removeMobileOptimizations();
  },
  watch: {
    '$route': {
      handler() {
        // Chỉ update khi thực sự cần thiết
        if (this.shouldShowMobileTabs) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
      immediate: false
    },
    windowWidth: {
      handler(newWidth, oldWidth) {
        // Chỉ update khi có thay đổi đáng kể về mobile/desktop
        const wasMobile = oldWidth <= 768;
        const isMobile = newWidth <= 768;
        
        if (wasMobile !== isMobile) {
          this.$nextTick(() => {
            this.$forceUpdate();
          });
        }
      },
      immediate: false
    }
  },
  computed: {
    shouldShowMobileTabs() {
      // Kiểm tra nếu là mobile device
      const isMobile = this.windowWidth <= 768;
      const currentRoute = this.$route.name;
      
      // Chỉ hiển thị trên mobile và các trang main
      const isMainRoute = currentRoute && currentRoute.startsWith('main.');
      const isNotCustomerVendor = !currentRoute.includes('customer') && !currentRoute.includes('vendor');
      
      // Đảm bảo windowWidth đã được khởi tạo
      const hasValidWidth = this.windowWidth > 0;
      
      return isMobile && isMainRoute && isNotCustomerVendor && hasValidWidth;
    },
  },
  methods: {
    handleResize() {
      // Throttle resize events để tránh quá nhiều updates
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      this.resizeTimeout = setTimeout(() => {
        const newWidth = window.innerWidth;
        const wasMobile = this.windowWidth <= 768;
        const isMobile = newWidth <= 768;
        
        this.windowWidth = newWidth;
        
        // Apply/remove mobile optimizations based on screen size
        if (wasMobile !== isMobile) {
          if (isMobile) {
            applyMobileOptimizations();
          } else {
            removeMobileOptimizations();
          }
        }
      }, 100);
    },
    isActiveTab(tab) {
      return this.$route.name === tab.route;
    },
    onTabClick(tab) {
      // Preload related routes for better performance
      preloadRelatedRoutes(tab.route);
      
      const query = {
        ...this.$route.query,
      };
      
      // Clear pagination and search params
      delete query.page;
      delete query.origin;
      delete query.page_size;
      delete query.search_text;
      delete query.exclude_invoice_types;
      
      // Set specific query params for different invoice types
      if (tab.id === 'issued') {
        query.invoice_outgoing = true;
        query.invoiceType = 'OUTGOING';
      } else if (tab.id === 'incoming') {
        query.invoice_outgoing = false;
        query.invoiceType = 'INCOMING';
      }
      
      // Hide statistics for invoice pages, show for dashboard
      if (tab.id === 'dashboard') {
        delete query.statistics;
      } else {
        query.statistics = 'HIDE';
      }
      
      // Navigate to the appropriate route
      this.$router.push({ name: tab.route, query }).catch(() => {});
    },
  },
};
</script>

<style lang="sass" scoped>
.mobile-tabs-container
  position: fixed
  bottom: 0
  left: 0
  right: 0
  z-index: 1000
  background: #fff
  border-top: 1px solid #e0e0e0
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1)

.mobile-tabs
  display: flex
  justify-content: space-around
  align-items: center
  padding: 8px 0
  max-width: 100%

.mobile-tab
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  padding: 8px 4px
  min-width: 60px
  cursor: pointer
  transition: background-color 0.15s ease-out
  border-radius: 8px
  will-change: background-color
  
  &:hover
    background-color: #f5f5f5
  
  &.active
    background-color: #e3f2fd
    
    .tab-label
      color: #005DCA
      font-weight: 600

.tab-label
  font-size: 10px
  color: #807D7B
  margin-top: 4px
  text-align: center
  line-height: 1.2
  transition: color 0.15s ease-out
  will-change: color

// Ensure tabs are visible on mobile
@media screen and (min-width: 769px)
  .mobile-tabs-container
    display: none
</style>



// WEBPACK FOOTER //
// src/components/app-mobile-tabs/index.vue